import axios, { AxiosInstance, AxiosResponse } from 'axios';

import { getBaseURL } from '@/utils/getBaseURL.util';

import { ApprovalApproveDTO } from '@/dto/approval.dto';
import {
  PutBorrowingEditExtensionDTO,
  PutBorrowingExtendRequestDTO,
} from '@/dto/borrowingExtend.dto';
import {
  PutBorrowingConfirmReportDoneDTO,
  PutBorrowingReportDamagedDTO,
  PutBorrowingReportMissingDTO,
} from '@/dto/borrowingReport.dto';
import {
  PostBorrowingPrelistRequestDTO,
  UpdateBorrowingPrelistRequestDTO,
} from '@/dto/borrowingRequest.dto';
import {
  PostBorrowingTransactionDTO,
  PutBorrowingHandoverConfirmationDTO,
  PutBorrowingTransactionDTO,
  PutUpdateEmailorBorrowerDTO,
} from '@/dto/borrowingTransaction.dto';
import {
  BorrowingPrelistLoadingFilters,
  BorrowingRequestLoadingFilters,
  BorrowingTransactionLoadingFilters,
  LoadingFilters,
} from '@/types/filterOptions.type';
import {
  BorrowingPrelistFetchParams,
  BorrowingRequestFetchParams,
  BorrowingTransactionFetchParams,
  FetchParams,
} from '@/types/params.type';
import {
  ApprovalTransactionResponse,
  BorrowingBorrowedByAssetResponse,
  BorrowingPrelistResponse,
  BorrowingRequestResponse,
  BorrowingTransactionDetailResponse,
  BorrowingTransactionLogResponse,
  BorrowingTransactionReponse,
  BorrowingTransactionRequestReponse,
  BorrowingTransactionScanResponse,
} from '@/types/response.type';

export const API = ({ headers = {}, params = {} } = {}): AxiosInstance => {
  const user = JSON.parse(localStorage.getItem('user') as string) ?? {};
  const BASE_URL = getBaseURL('APP_BORROWING_API');

  const instance = axios.create({
    baseURL: `${BASE_URL}/v2`,
    headers: {
      'Content-type': 'application/json',
      'Authorization': `Bearer ${user.token}`,
      ...headers,
    },
    params,
  });

  return instance;
};

const BorrowingServices = {
  getBorrowingPrelist: (
    params: BorrowingPrelistFetchParams,
  ): Promise<AxiosResponse<BorrowingPrelistResponse>> => {
    return API({ params }).get('/prelist');
  },

  getBorrowingPrelistOptions: (
    params: BorrowingPrelistLoadingFilters,
  ): Promise<AxiosResponse> => {
    return API({ params }).get('prelist/options');
  },

  deleteBorrowingPrelist: (id: string[]): Promise<AxiosResponse> => {
    return API().delete('/prelist', { data: { id } });
  },

  getBorrowingRequest: (
    params: BorrowingRequestFetchParams,
  ): Promise<AxiosResponse<BorrowingRequestResponse>> => {
    return API({ params }).get('/prelist/request');
  },

  getBorrowingRequestOptions: (
    params: BorrowingRequestLoadingFilters & { prelist: string },
  ): Promise<AxiosResponse> => {
    return API({ params }).get('/prelist/request/options');
  },

  postBorrowingRequest: (
    data: PostBorrowingPrelistRequestDTO[],
  ): Promise<AxiosResponse> => {
    return API().post('/prelist/request', data);
  },

  putBorrowingRequest: (
    data: UpdateBorrowingPrelistRequestDTO[],
  ): Promise<AxiosResponse> => {
    return API().put('/prelist/request', data);
  },

  deleteBorrowingRequest: (id: string[]): Promise<AxiosResponse> => {
    return API().delete('/prelist/request', { data: { id } });
  },

  postBorrowingTransaction: (
    data: PostBorrowingTransactionDTO[],
  ): Promise<AxiosResponse> => {
    return API({ params: { sourceWeb: true } }).post('/transaction', data);
  },

  getBorrowingTransaction: (
    params: BorrowingTransactionFetchParams,
  ): Promise<AxiosResponse<BorrowingTransactionReponse>> => {
    return API({ params }).get('/transaction');
  },

  getBorrowingTransactionOptions: (
    params: BorrowingTransactionLoadingFilters,
  ): Promise<AxiosResponse> => {
    return API({ params }).get('/transaction/options');
  },

  putCancelBorrowingTransaction: (id: string[]): Promise<AxiosResponse> => {
    return API().put('/transaction/cancel', { id });
  },

  putCancelBorrowingRequest: (id: string[]): Promise<AxiosResponse> => {
    return API().put('/transaction/request/cancel', { id });
  },

  putCancelExtendBorrowingRequest: (id: string[]): Promise<AxiosResponse> => {
    return API().put('/transaction/request/extend/cancel', { id });
  },

  getBorrowingTransactionDetail: (
    id: string,
  ): Promise<AxiosResponse<BorrowingTransactionDetailResponse>> => {
    return API().get(`/transaction/${id}`);
  },

  getBorrowingTransactionRequestList: (
    params?: Omit<BorrowingRequestFetchParams, 'prelist'>,
  ): Promise<AxiosResponse<BorrowingTransactionRequestReponse>> => {
    return API({ params }).get('/transaction/request');
  },

  getBorrowingTransactionRequest: (
    id: string,
    params?: Omit<BorrowingRequestFetchParams, 'prelist'>,
  ): Promise<AxiosResponse<BorrowingTransactionRequestReponse>> => {
    return API({ params }).get(`/transaction/${id}/request`);
  },

  getBorrowingTransactionRequestOptions: (
    id: string,
    params: Omit<BorrowingRequestFetchParams, 'prelist'>,
  ): Promise<AxiosResponse> => {
    return API({ params }).get(`/transaction/${id}/request/options`);
  },

  putBorrowingTransactionRequest: (
    id: string,
    data: PutBorrowingTransactionDTO[],
  ): Promise<AxiosResponse> => {
    return API().put(`/transaction/${id}/request`, data);
  },

  getApprovalList: (
    id: string,
  ): Promise<AxiosResponse<ApprovalTransactionResponse>> => {
    return API().get(`/approval/transaction/${id}/transaction`);
  },

  putUpdateEmailorBorrower: (
    id: string,
    data: PutUpdateEmailorBorrowerDTO,
  ): Promise<AxiosResponse> => {
    return API().put(`/transaction/${id}/update-email-or-borrower`, data);
  },

  getTransactionRequestScan: (
    id: string,
    tag: string,
  ): Promise<AxiosResponse<BorrowingTransactionScanResponse>> => {
    return API({ params: { tag } }).get(`/transaction/${id}/request/scan`);
  },

  putUpdateBorrower: (id: string, userId: string): Promise<AxiosResponse> => {
    return API().put(`/transaction/${id}/user`, { user: userId });
  },

  postSendConfirmationEmail: (id: string): Promise<AxiosResponse> => {
    return API().post(`/transaction/${id}/send-confirmation-email`);
  },

  putVerifyRequests: (
    id: string,
    data: {
      verifiedRequests: {
        id: string;
        serialNumber: string;
      }[];
    },
  ): Promise<AxiosResponse> => {
    return API().put(`/transaction/${id}/verify-requests`, data);
  },

  getBorrowingTransactionHistoryByTransaction: (
    params: BorrowingTransactionFetchParams,
  ): Promise<AxiosResponse<BorrowingTransactionReponse>> => {
    return API({ params }).get('/transaction/history/by-transaction');
  },

  getBorrowingTransactionHistoryByAsset: (
    params: Record<string, string | number>,
  ): Promise<AxiosResponse> => {
    return API({ params }).get('/transaction/history/by-asset');
  },

  getBorrowingTransactionHistoryOptions: (
    params: BorrowingTransactionLoadingFilters,
  ): Promise<AxiosResponse> => {
    return API({ params }).get('/transaction/history/options');
  },

  putBorrowingVerifyToken: (token: string): Promise<AxiosResponse> => {
    return API().put('/transaction/verify-token', { token });
  },

  putBorrowingHandoverConfirmation: (
    data: PutBorrowingHandoverConfirmationDTO,
  ): Promise<AxiosResponse> => {
    return API().put('/transaction/handover-confirmation', data);
  },

  putBorrowingHandover: (id: string): Promise<AxiosResponse> => {
    return API().put(`/transaction/${id}/handover`);
  },

  putBorrowingExtendRequest: (
    data: PutBorrowingExtendRequestDTO[],
  ): Promise<AxiosResponse> => {
    return API().put('/transaction/request/extend', data);
  },

  putBorrowingExtendApproval: (
    data: ApprovalApproveDTO[],
  ): Promise<AxiosResponse> => {
    return API({ params: { sourceWeb: true } }).put(
      '/approval/approve/request-extension',
      data,
    );
  },

  putBorrowingDeclineExtendRequest: (id: string[]): Promise<AxiosResponse> => {
    return API().put('/transaction/request/decline', { id });
  },

  getBorrowingBorrowedAsset: (
    params: FetchParams,
  ): Promise<AxiosResponse<BorrowingBorrowedByAssetResponse>> => {
    return API({ params }).get('/transaction/request/borrowed/by-asset');
  },

  getBorrowingBorrowedAssetOptions: (
    params: LoadingFilters & { group?: string; borrower?: string },
  ): Promise<AxiosResponse> => {
    return API({ params }).get(
      '/transaction/request/borrowed/by-asset/options',
    );
  },

  getBorrowingBorrowedBorrower: (
    params: FetchParams,
  ): Promise<AxiosResponse> => {
    return API({ params }).get('/transaction/request/borrowed/by-user');
  },

  putBorrowingReportDamaged: (
    id: string,
    data: PutBorrowingReportDamagedDTO,
  ): Promise<AxiosResponse> => {
    return API().put(`/transaction/request/${id}/damaged`, data);
  },

  putBorrowingReportMissing: (
    id: string,
    data: PutBorrowingReportMissingDTO,
  ): Promise<AxiosResponse> => {
    return API().put(`/transaction/request/${id}/missing`, data);
  },

  putBorrowingReturn: (id: string[]): Promise<AxiosResponse> => {
    return API().put('/transaction/request/return', { id });
  },

  getBorrowingLog: (
    id: string,
  ): Promise<AxiosResponse<BorrowingTransactionLogResponse>> => {
    return API().get(`/transaction/request/${id}/transaction-log`);
  },

  putConfirmReportDone: (
    id: string,
    data: PutBorrowingConfirmReportDoneDTO,
  ): Promise<AxiosResponse> => {
    return API().put(`/transaction/request/${id}/confirm-report-done`, data);
  },

  putCancelReportBulk: (id: string[]): Promise<AxiosResponse> => {
    return API().put('/transaction/request/cancel-report', {
      id,
    });
  },

  putBorrowingEditExtension: (
    data: PutBorrowingEditExtensionDTO[],
  ): Promise<AxiosResponse> => {
    return API().put('/transaction/request/duration', data);
  },
};

export default BorrowingServices;
